import type {
  OrganizationMember,
  OrganizationPendingInvite,
} from "../../lib/api/users";

export type MembershipRowKind = "active" | "pending" | "invited";

export interface MembershipRow {
  id: string;
  kind: MembershipRowKind;
  displayName: string;
  firstName?: string;
  lastName?: string;
  email: string;
  avatarUrl?: string | null;
  roleId?: string | null;
  roleName?: string | null;
  roleLabel?: string | null;
  roleColor?: string | null;
  inviteExpiresAt?: string | null;
  deletedAt?: string | null;
  createdAt?: string;
}

const SORT_ORDER: Record<MembershipRowKind, number> = {
  pending: 0,
  invited: 1,
  active: 2,
};

export function toMembershipRows(
  members: OrganizationMember[],
  invites: OrganizationPendingInvite[],
  isOrgAdmin: boolean,
): MembershipRow[] {
  const rows: MembershipRow[] = members.map((m) => ({
    id: m.userId,
    kind: m.membershipStatus as "active" | "pending",
    displayName:
      [m.firstName, m.lastName].filter(Boolean).join(" ").trim() || m.email,
    firstName: m.firstName,
    lastName: m.lastName,
    email: m.email,
    avatarUrl: m.avatarUrl ?? null,
    roleId: m.roleId ?? null,
    roleName: m.roleName ?? null,
    roleLabel: m.roleLabel ?? null,
    roleColor: m.roleColor ?? null,
    deletedAt: m.deletedAt ?? null,
    createdAt: m.createdAt,
  }));

  if (isOrgAdmin) {
    for (const inv of invites) {
      rows.push({
        id: inv.inviteId,
        kind: "invited",
        displayName: inv.email,
        email: inv.email,
        roleId: inv.roleId,
        roleName: inv.roleName ?? null,
        roleColor: inv.roleColor ?? null,
        inviteExpiresAt: inv.expiresAt,
      });
    }
  }

  return rows.slice().sort((a, b) => SORT_ORDER[a.kind] - SORT_ORDER[b.kind]);
}

export function formatMemberRoleLabel(
  roleName: string | null | undefined,
  roleLabel?: string | null,
): string {
  const label = roleLabel?.trim();
  if (label) return label;
  if (!roleName) return "";
  return roleName
    .replace(/^pro:/, "")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatInviteExpiry(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Invitation expires soon";
  return `Expires ${d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  })}`;
}
